import { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'

/**
 * Welding-spark cursor. A canvas trail throws sparks off the pointer as it moves:
 * a white-hot arc-flash tip, sparks streaking in a cone behind the motion, arcing
 * under gravity, flickering, and bursting into tiny carbon-star forks the way real
 * grinding/arc-weld sparks crackle. Desktop/fine-pointer only, off under
 * prefers-reduced-motion. Never intercepts clicks.
 */

// White-hot → gold → ember, matching the brand's molten-gold accent.
const SPARK_COLORS = ['#FFFFFF', '#FFF7E0', '#FFE08A', '#D4AF37', '#FF8A1E', '#FF5A1E']
const MAX_SPARKS = 150

interface Spark {
  x: number
  y: number
  vx: number
  vy: number
  life: number
  maxLife: number
  size: number
  color: string
  forkAt: number // life at which it bursts into a carbon-star (0 = never)
}

export function CustomCursor() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [on, setOn] = useState(false)

  useEffect(() => {
    const fine = window.matchMedia('(pointer: fine)').matches
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    setOn(fine && !reduced)
  }, [])

  useEffect(() => {
    if (!on) return
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    let w = 0
    let h = 0
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      w = window.innerWidth
      h = window.innerHeight
      canvas.width = w * dpr
      canvas.height = h * dpr
      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    const sparks: Spark[] = []
    let mx = -100
    let my = -100
    let hasMouse = false

    const spawn = (x: number, y: number, vx: number, vy: number, hot: boolean): void => {
      const maxLife = hot ? 22 + Math.random() * 22 : 10 + Math.random() * 12
      sparks.push({
        x,
        y,
        vx,
        vy,
        life: 0,
        maxLife,
        size: (hot ? 0.8 : 0.5) + Math.random() * (hot ? 1.6 : 0.9),
        color: SPARK_COLORS[(Math.random() * SPARK_COLORS.length) | 0],
        // ~8% of primary sparks burst partway through their life.
        forkAt: hot && Math.random() < 0.08 ? maxLife * (0.4 + Math.random() * 0.3) : 0,
      })
    }

    // Emit a cone of sparks trailing behind the pointer's motion.
    const emit = (x: number, y: number, dx: number, dy: number, speed: number) => {
      const dir = Math.atan2(dy, dx) + Math.PI // opposite to travel = trailing
      const count = Math.min(4, 1 + Math.floor(speed / 10))
      for (let i = 0; i < count; i++) {
        const ang = dir + (Math.random() - 0.5) * 1.0 // ~±0.5 rad cone
        const spd = 1.0 + Math.random() * 2.2 // moderate throw
        spawn(x, y, Math.cos(ang) * spd, Math.sin(ang) * spd - 0.4, true)
      }
      while (sparks.length > MAX_SPARKS) sparks.shift()
    }

    const move = (e: MouseEvent) => {
      const dx = e.clientX - mx
      const dy = e.clientY - my
      mx = e.clientX
      my = e.clientY
      const first = !hasMouse
      hasMouse = true
      const speed = Math.hypot(dx, dy)
      if (!first && speed > 2.5) emit(mx, my, dx, dy, speed)
    }
    window.addEventListener('mousemove', move, { passive: true })

    let raf = 0
    const tick = () => {
      ctx.clearRect(0, 0, w, h)
      ctx.globalCompositeOperation = 'lighter' // additive glow
      ctx.lineCap = 'round'

      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i]
        s.life++
        if (s.life >= s.maxLife) {
          sparks.splice(i, 1)
          continue
        }
        s.vy += 0.14 // gravity
        s.vx *= 0.93 // air drag — sparks slow and settle near the cursor
        s.vy *= 0.93
        const px = s.x
        const py = s.y
        s.x += s.vx
        s.y += s.vy

        // Carbon-star burst: split into a little radial shower once, mid-flight.
        if (s.forkAt && s.life >= s.forkAt) {
          s.forkAt = 0
          const n = 3 + ((Math.random() * 3) | 0)
          for (let k = 0; k < n; k++) {
            const a = Math.random() * Math.PI * 2
            const sp = 0.6 + Math.random() * 1.8
            spawn(s.x, s.y, Math.cos(a) * sp, Math.sin(a) * sp, false)
          }
        }

        const t = 1 - s.life / s.maxLife // 1 → 0 fade
        ctx.strokeStyle = s.color
        ctx.globalAlpha = t * (0.35 + Math.random() * 0.3) // per-frame flicker = crackle (lighter)
        ctx.lineWidth = s.size * t
        ctx.beginPath()
        ctx.moveTo(px, py) // streak from previous to current = motion-blurred spark
        ctx.lineTo(s.x, s.y)
        ctx.stroke()
      }

      // White-hot arc-flash tip at the cursor (flickers like a live arc).
      if (hasMouse) {
        const flick = 0.5 + Math.random() * 0.35
        const r = 5 + Math.random() * 2
        const grd = ctx.createRadialGradient(mx, my, 0, mx, my, r)
        grd.addColorStop(0, `rgba(220,235,255,${0.7 * flick})`)
        grd.addColorStop(0.35, `rgba(255,240,200,${0.4 * flick})`)
        grd.addColorStop(0.7, 'rgba(212,175,55,0.22)')
        grd.addColorStop(1, 'rgba(212,175,55,0)')
        ctx.globalAlpha = 1
        ctx.fillStyle = grd
        ctx.beginPath()
        ctx.arc(mx, my, r, 0, Math.PI * 2)
        ctx.fill()
      }

      ctx.globalAlpha = 1
      ctx.globalCompositeOperation = 'source-over'
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
      window.removeEventListener('mousemove', move)
    }
  }, [on])

  if (!on) return null

  return (
    <Box
      component="canvas"
      ref={canvasRef}
      aria-hidden
      sx={{ position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none' }}
    />
  )
}
